import React from 'react';
import { motion } from 'framer-motion';
import { useFormatDimensions } from '@/hooks/content-generator/private-image-library';
import AddToLibraryButton from '@/components/universal-storage/AddToLibraryButton';
import ProjectImageAddToProjectButton from './ProjectImageAddToProjectButton';
import type { PrivateImageCardProps as BasePrivateImageCardProps } from '@/hooks/content-generator/private-image-library/types';

interface PrivateImageCardProps extends BasePrivateImageCardProps {
  workbookId?: string; // NEW: Pass workbook context for project assignment state
  hideStorageButton?: boolean; // NEW: Optional prop to hide storage button
}

const PrivateImageCard: React.FC<PrivateImageCardProps> = ({
  image,
  index,
  onClick,
  workbookId, // NEW: Destructure workbookId
  hideStorageButton // NEW: Destructure hideStorageButton
}) => {
  const formatDimensions = useFormatDimensions();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onClick={() => onClick(index)}
      className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group hover:shadow-lg transition-all duration-300"
    >
      {/* Image */}
      <img
        src={image.image_url}
        alt={image.alt_text || 'Project image'}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        loading="lazy"
      />
      
      {/* Action buttons - bottom right */}
      <div className="absolute bottom-2 right-2 z-10 flex gap-1">
        {/* Project assignment button */}
        <motion.div 
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: 1,
            transition: {
              type: "spring",
              stiffness: 180,
              damping: 28,
              delay: index * 0.05 + 0.05
            }
          }}
          style={{ willChange: 'transform, opacity' }}
          onClick={(e) => e.stopPropagation()}
        >
          <ProjectImageAddToProjectButton
            imageId={image.image_id}
            currentProjectId={image.workbook_id}
            dropdownLabel="Add to Project"
            variant="auto"
            forceWhiteText={true}
          />
        </motion.div>

        {/* Storage button */}
        {!hideStorageButton && (
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              transition: {
                type: "spring",
                stiffness: 180,
                damping: 28,
                delay: index * 0.05 + 0.1
              }
            }}
            style={{ willChange: 'transform, opacity' }}
            onClick={(e) => e.stopPropagation()}
          >
            <AddToLibraryButton
              itemId={image.image_id}
              contentType="private_image"
              dropdownLabel="Add to Storage"
              variant="auto"
              forceWhiteText={true}
            />
          </motion.div>
        )}
      </div>
      
      {/* Overlay with metadata on hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-end">
        <div className="p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-full">
          <p className="text-xs font-medium truncate mb-1">
            {image.generation_prompt || image.alt_text || image.filename}
          </p>
          <p className="text-xs opacity-75">
            {formatDimensions(image.width, image.height)}
          </p>
        </div>
      </div>
      

    </motion.div>
  );
};

export default PrivateImageCard; 